import { FormEvent, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader, Pill } from '../components/ui'
import { useAuthFetch } from '../lib/useAuthFetch'

const API = import.meta.env.VITE_API_URL ?? ''

interface Evidence { label: string; href: string; as_of?: string | null }
interface Message { id?: string; role: 'user' | 'assistant'; body: string; evidence?: Evidence[]; created_at?: string }
interface Conversation { id: string; title: string; updated_at: string }
interface Location { id: string; name: string }
interface Suggestion { eyebrow: string; question: string; detail: string }

const sectionLabels: Record<string, string> = {
  answer: 'Answer',
  'what changed': 'What changed',
  'why it matters': 'Why it matters',
  'next step': 'Next step',
}

function parseAnswer(body: string): Array<{ label: string; body: string }> {
  const sections: Array<{ label: string; body: string }> = []
  let current: { label: string; lines: string[] } | null = null
  for (const line of body.split('\n')) {
    const normalized = line.replace(/^#{1,4}\s*/, '').replace(/^\*\*(.+)\*\*:?$/, '$1').replace(/:$/, '').trim().toLowerCase()
    const label = sectionLabels[normalized]
    if (label) {
      if (current?.lines.join('\n').trim()) sections.push({ label: current.label, body: current.lines.join('\n').trim() })
      current = { label, lines: [] }
    } else if (current) current.lines.push(line)
  }
  if (current?.lines.join('\n').trim()) sections.push({ label: current.label, body: current.lines.join('\n').trim() })
  return sections.length >= 2 ? sections : []
}

function formatObservedAt(value?: string | null) {
  if (!value) return 'Observation time unavailable'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return 'Observation time unavailable'
  return `Observed ${parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
}

function AnswerBody({ message }: { message: Message }) {
  const sections = parseAnswer(message.body)
  if (!sections.length) return <div className="analyst-answer-copy">{message.body}</div>
  return <div className="analyst-answer-sections">
    {sections.map((section) => <section key={section.label}>
      <div className="analyst-section-label">{section.label}</div>
      <div className="analyst-answer-copy">{section.body}</div>
    </section>)}
  </div>
}

export default function Analyst() {
  const authFetch = useAuthFetch()
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [locations, setLocations] = useState<Location[]>([])
  const [conversationId, setConversationId] = useState<string | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const endRef = useRef<HTMLDivElement>(null)

  const loadConversations = async () => {
    const r = await authFetch(`${API}/api/v1/analyst/conversations`)
    if (r.ok) setConversations((await r.json()).data ?? [])
  }
  useEffect(() => { loadConversations().catch(() => {}) }, [])
  useEffect(() => {
    authFetch(`${API}/api/v1/locations`)
      .then((r) => r.ok ? r.json() : Promise.reject(new Error('Locations unavailable')))
      .then((json) => setLocations(json.locations ?? []))
      .catch(() => setLocations([]))
  }, [authFetch])
  useEffect(() => {
    if (messages.length || sending) endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, sending])

  const suggestions = useMemo<Suggestion[]>(() => {
    const primary = locations[0]?.name
    const secondary = locations[1]?.name
    const near = primary ? ` near ${primary}` : ''
    const brandScope = primary && secondary ? ` near ${primary} and ${secondary}` : near
    return [
      {
        eyebrow: 'PRICE EXPOSURE',
        question: primary ? `Where are we most exposed at ${primary}?` : 'Where are we most exposed?',
        detail: 'Compare exact-name price disadvantages and assortment gaps.',
      },
      {
        eyebrow: 'CATEGORY POSITION',
        question: `Who has the best concentrate selection and prices${near}?`,
        detail: 'Rank tracked rivals by selection, brands, prices, and promotions.',
      },
      {
        eyebrow: 'BRAND COVERAGE',
        question: `Who carries Dime${brandScope}?`,
        detail: 'Find tracked carriers, selection depth, and observed price range.',
      },
    ]
  }, [locations])

  async function openConversation(id: string) {
    setConversationId(id); setError(null)
    const r = await authFetch(`${API}/api/v1/analyst/${id}/messages`)
    if (r.ok) setMessages((await r.json()).data ?? [])
  }

  async function ask(text: string) {
    const question = text.trim()
    if (!question || sending) return
    setDraft(''); setError(null); setSending(true)
    setMessages((m) => [...m, { role: 'user', body: question }])
    try {
      const r = await authFetch(`${API}/api/v1/analyst/message`, {
        method: 'POST', body: JSON.stringify({ conversation_id: conversationId, message: question }),
      })
      const json = await r.json()
      if (!r.ok) throw new Error(json.error || 'The analyst could not answer right now.')
      setConversationId(json.data.conversation_id)
      setMessages((m) => [...m, { id: json.data.id, role: 'assistant', body: json.data.answer, evidence: json.data.evidence, created_at: json.data.created_at }])
      await loadConversations()
    } catch (err: any) {
      setError(err.message)
    } finally { setSending(false) }
  }

  function submit(e: FormEvent) { e.preventDefault(); ask(draft) }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <PageHeader title="CannaSpy Analyst" subtitle="Ask the live market. Answers are grounded in your tracked locations and current menu data." actions={
        <button className="btn btn-ghost" onClick={() => { setConversationId(null); setMessages([]); setError(null) }}>New analysis</button>
      } />
      <div style={{ display: 'grid', gridTemplateColumns: '260px minmax(0, 1fr)', minHeight: 0, flex: 1 }} className="analyst-shell">
        <aside style={{ borderRight: '1px solid var(--border)', padding: 14, overflowY: 'auto', background: 'var(--surface)' }}>
          <div className="mono" style={{ fontSize: 9, letterSpacing: '.14em', color: 'var(--text-3)', margin: '4px 6px 10px' }}>RECENT ANALYSIS</div>
          {conversations.map((c) => <button key={c.id} onClick={() => openConversation(c.id)} style={{ width: '100%', border: 'none', borderRadius: 8, textAlign: 'left', padding: '9px 10px', marginBottom: 4, cursor: 'pointer', fontFamily: 'var(--sans)', fontSize: 12, color: conversationId === c.id ? 'var(--accent)' : 'var(--text-2)', background: conversationId === c.id ? 'var(--accent-soft)' : 'transparent' }}>{c.title}</button>)}
          {!conversations.length && <div style={{ padding: 8, color: 'var(--text-3)', fontSize: 12 }}>Your saved analyses will appear here.</div>}
        </aside>
        <section style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ flex: 1, overflowY: 'auto', padding: '28px clamp(18px, 5vw, 72px)' }}>
            {!messages.length && <div className="analyst-start" style={{ maxWidth: 860, margin: 'clamp(24px, 7vh, 76px) auto 0' }}>
              <div className="mono" style={{ color: 'var(--accent)', fontSize: 10, letterSpacing: '.16em', marginBottom: 10 }}>LIVE COMPETITIVE INTELLIGENCE</div>
              <h1 style={{ fontFamily: 'var(--display)', fontSize: 'clamp(34px, 4vw, 48px)', fontWeight: 400, color: 'var(--text-1)', margin: '0 0 10px', lineHeight: 1.08 }}>Choose the decision you need to make.</h1>
              <p style={{ color: 'var(--text-2)', lineHeight: 1.6, margin: '0 0 12px', maxWidth: 650 }}>Start with a scoped market question. Each answer separates the finding, change context, business meaning, and next step.</p>
              <div className="analyst-scope-line">
                <span>{locations.length ? `${locations.length} authorized location${locations.length === 1 ? '' : 's'}` : 'Authorized locations only'}</span>
                <span>Tracked-market records</span>
                <span>Sources shown with every answer</span>
              </div>
              <div className="analyst-suggestion-grid">{suggestions.map((s) => <button key={s.question} type="button" className="analyst-suggestion" onClick={() => ask(s.question)}>
                <span className="analyst-suggestion-eyebrow">{s.eyebrow}</span>
                <strong>{s.question}</strong>
                <span>{s.detail}</span>
                <em>Run analysis →</em>
              </button>)}</div>
            </div>}
            <div style={{ maxWidth: 820, margin: '0 auto' }}>
              {messages.map((m, i) => <div key={m.id ?? i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: 18 }}>
                <div className={m.role === 'assistant' ? 'analyst-response' : undefined} style={{ maxWidth: m.role === 'user' ? '72%' : '94%', padding: m.role === 'user' ? '11px 14px' : '20px 22px', borderRadius: 12, background: m.role === 'user' ? 'var(--accent)' : 'var(--surface)', color: m.role === 'user' ? '#fff' : 'var(--text-1)', border: m.role === 'user' ? 'none' : '1px solid var(--border-2)', whiteSpace: 'pre-wrap', lineHeight: 1.62, fontSize: 13.5 }}>
                  {m.role === 'assistant' && <Pill tone="accent">ANALYST</Pill>}
                  <div style={{ marginTop: m.role === 'assistant' ? 12 : 0 }}>{m.role === 'assistant' ? <AnswerBody message={m} /> : m.body}</div>
                  {m.role === 'assistant' && <div className="analyst-grounding">
                    <div><strong>Grounding</strong><span>{m.evidence?.length ? `${m.evidence.length} tracked source${m.evidence.length === 1 ? '' : 's'}` : 'No linked source records returned'}</span></div>
                    <div><strong>Answer generated</strong><span>{formatObservedAt(m.created_at).replace('Observed', '')}</span></div>
                  </div>}
                  {!!m.evidence?.length && <div className="analyst-evidence-list">{m.evidence.map((e) => <Link key={e.href} to={e.href}>
                    <span>{e.label}</span><small>{formatObservedAt(e.as_of)}</small><em>Open evidence →</em>
                  </Link>)}</div>}
                  {m.role === 'assistant' && <div className="analyst-response-actions"><Link to="/today">Open Today</Link><button type="button" onClick={() => setDraft(`Follow up on this finding: ${m.body.slice(0, 140)}`)}>Ask a follow-up</button></div>}
                </div>
              </div>)}
              {sending && <div style={{ color: 'var(--text-3)', fontFamily: 'var(--mono)', fontSize: 11 }}>Querying current market data…</div>}
              {error && <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 10 }}>{error}</div>}
              <div ref={endRef} />
            </div>
          </div>
          <form onSubmit={submit} style={{ padding: '14px clamp(18px, 5vw, 72px) 20px', borderTop: '1px solid var(--border)', background: 'var(--surface)' }}>
            <div style={{ display: 'flex', maxWidth: 820, margin: '0 auto', gap: 10 }}>
              <textarea value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(draft) } }} placeholder="Ask about a brand, market, price, or exposure…" rows={2} style={{ flex: 1, resize: 'none', border: '1px solid var(--border-2)', borderRadius: 10, background: 'var(--surface-2)', color: 'var(--text-1)', padding: '11px 13px', font: '13px var(--sans)', outline: 'none' }} />
              <button className="btn btn-primary" disabled={sending || !draft.trim()} type="submit">Ask analyst</button>
            </div>
          </form>
        </section>
      </div>
    </div>
  )
}
